import { useEffect, useState } from "react";
import Modal from "../../../components/fundamentals/modal";
import Spinner from "../../../components/fundamentals/spinner";
import categoryService from "../../../services/category.service";
import { Category } from "../../../models/Category";

interface IProps {
  title: string;
  onClose: () => void;
  onPick: (category: Category) => void;
}

/**
 * RAG-30 — Sélecteur de catégorie cible : recherche par nom (l'arbre complet fait
 * plusieurs milliers de catégories, on privilégie la recherche).
 */
export default function CategoryPicker({ title, onClose, onPick }: IProps) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (term.trim().length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        setResults(await categoryService.list(1, term));
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [term]);

  return (
    <Modal handleClose={onClose} isLargeModal>
      <Modal.Body>
        <Modal.Header handleClose={onClose}>
          <h1 className="inter-xlarge-semibold">{title}</h1>
        </Modal.Header>
        <Modal.Content>
          <input
            autoFocus
            className="bg-grey-5 border border-gray-20 rounded-rounded h-10 w-full px-small outline-none focus:border-blue-60"
            placeholder="Rechercher une catégorie par son nom..."
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />

          {loading && (
            <div className="py-base flex justify-center">
              <Spinner />
            </div>
          )}

          {!loading && term.trim().length >= 2 && results.length === 0 && (
            <p className="py-base text-grey-50 inter-small-regular">Aucune catégorie trouvée.</p>
          )}

          <div className="mt-base max-h-[320px] overflow-y-auto flex flex-col gap-y-1">
            {results.map((category) => (
              <button
                key={category.id}
                type="button"
                className="flex items-center justify-between text-left px-small py-2 rounded-rounded hover:bg-grey-10"
                onClick={() => onPick(category)}
              >
                <span className="inter-base-regular text-grey-90">{category.name}</span>
                <span className="inter-small-regular text-grey-40">{category.id}</span>
              </button>
            ))}
          </div>
        </Modal.Content>
      </Modal.Body>
    </Modal>
  );
}
